import React, { useContext, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FaBars,
  FaCaretDown,
  FaHome,
  FaInfoCircle,
  FaSearch,
  FaSignInAlt,
  FaSignOutAlt,
  FaThLarge,
  FaTimes,
  FaUserCircle,
  FaUserPlus,
} from 'react-icons/fa';
import { AppContext } from '../../context/AppContext';
import logo from '../../assets/logo.png';

const DEFAULT_EVENT_NAME = 'EVENT TAEKWONDO';

const navItems = [
  { label: 'Beranda', path: '/', icon: FaHome },
  { label: 'Informasi', path: '/informasi', icon: FaInfoCircle },
  { label: 'Cek Data', path: '/cek-data', icon: FaSearch },
];

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const { user, logout, settings, masterData } = useContext(AppContext);
  const location = useLocation();
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND || process.env.REACT_APP_BACKEND_URL || '';

  const eventName = settings?.event_name?.trim() || DEFAULT_EVENT_NAME;
  const eventYear = settings?.event_date
    ? new Date(settings.event_date).getFullYear()
    : masterData?.event_year || new Date().getFullYear();
  const logoSrc = settings?.web_logo ? `${backendUrl}/uploads/settings/${settings.web_logo}` : logo;
  const registrationOpen = settings?.is_registration_open !== false;

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 18);
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileOpen(false);
    setShowDropdown(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const dashboardLink = user?.role === 'manager' ? '/manager/dashboard' : '/admin/dashboard';

  return (
    <nav className={`navbar navbar-expand-lg public-navbar fixed-top ${isScrolled ? 'is-scrolled' : ''}`}>
      <div className="container">
        <Link to="/" className="navbar-brand public-navbar__brand">
          <span className="public-navbar__logo">
            <img src={logoSrc} alt={`Logo ${eventName}`} />
          </span>
          <span className="public-navbar__brand-text">
            <strong>{eventName}</strong>
            <small>Taekwondo Championship {eventYear}</small>
          </span>
        </Link>

        <button
          type="button"
          className="navbar-toggler public-navbar__toggle"
          aria-label="Toggle navigation"
          onClick={() => setIsMobileOpen((current) => !current)}
        >
          {isMobileOpen ? <FaTimes /> : <FaBars />}
        </button>

        <div className={`collapse navbar-collapse public-navbar__menu ${isMobileOpen ? 'show' : ''}`}>
          <ul className="navbar-nav mx-auto align-items-lg-center gap-lg-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;

              return (
                <li className="nav-item" key={item.path}>
                  <Link className={`nav-link public-navbar__link ${isActive ? 'active' : ''}`} to={item.path}>
                    <Icon className="public-navbar__link-icon" />
                    {item.label}
                  </Link>
                </li>
              );
            })}
          </ul>

          <div className="public-navbar__actions">
            {user ? (
              <div className="position-relative">
                <button
                  type="button"
                  className="public-navbar__user"
                  onClick={() => setShowDropdown((current) => !current)}
                >
                  <FaUserCircle className="text-success" />
                  <span>
                    <small>Halo,</small>
                    <strong>{user.name?.split(' ')[0] || 'User'}</strong>
                  </span>
                  <FaCaretDown className="text-muted" />
                </button>

                {showDropdown && (
                  <div className="public-navbar__dropdown">
                    <div className="public-navbar__dropdown-head">
                      <strong>{user.name || 'User'}</strong>
                      <small>{user.role || 'Akun'}</small>
                    </div>
                    <Link to={dashboardLink} className="public-navbar__dropdown-item">
                      <FaThLarge /> Dashboard
                    </Link>
                    <button type="button" onClick={handleLogout} className="public-navbar__dropdown-item danger">
                      <FaSignOutAlt /> Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link to="/login" className="btn public-navbar__login">
                  <FaSignInAlt /> Masuk
                </Link>
                {registrationOpen ? (
                  <Link to="/register" className="btn public-navbar__register">
                    <FaUserPlus /> Daftar
                  </Link>
                ) : (
                  <button type="button" className="btn public-navbar__register disabled" disabled>
                    <FaUserPlus /> Ditutup
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .public-navbar {
          background: rgba(255, 255, 255, 0.96);
          border-bottom: 1px solid rgba(22, 101, 52, 0.12);
          box-shadow: 0 14px 35px rgba(15, 23, 42, 0.08);
          padding: 0.95rem 0;
          transition: padding 0.25s ease, background 0.25s ease, box-shadow 0.25s ease;
          backdrop-filter: blur(14px);
        }

        .public-navbar.is-scrolled {
          padding: 0.55rem 0;
          background: rgba(255, 255, 255, 0.9);
          box-shadow: 0 18px 45px rgba(15, 23, 42, 0.12);
        }

        .public-navbar__brand {
          display: inline-flex;
          align-items: center;
          gap: 0.75rem;
          min-width: 0;
        }

        .public-navbar__logo {
          width: 52px;
          height: 52px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 18px;
          background: linear-gradient(135deg, #14532d, #166534);
          box-shadow: 0 12px 28px rgba(20, 83, 45, 0.24);
          overflow: hidden;
          flex: 0 0 auto;
        }

        .public-navbar__logo img {
          width: 48px;
          height: 48px;
          object-fit: contain;
        }

        .public-navbar__brand-text {
          display: flex;
          flex-direction: column;
          line-height: 1.05;
        }

        .public-navbar__brand-text strong {
          max-width: 270px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          color: #14532d;
          font-size: 1.18rem;
          font-weight: 900;
        }

        .public-navbar__brand-text small {
          color: #ea580c;
          font-size: 0.64rem;
          font-weight: 900;
          text-transform: uppercase;
          margin-top: 0.2rem;
        }

        .public-navbar__toggle {
          width: 46px;
          height: 46px;
          border: 1px solid rgba(22, 101, 52, 0.15);
          border-radius: 16px;
          color: #14532d;
          background: #f0fdf4;
          box-shadow: none;
        }

        .public-navbar__toggle:focus {
          box-shadow: 0 0 0 0.25rem rgba(22, 101, 52, 0.12);
        }

        .public-navbar__menu.show {
          margin-top: 1rem;
          padding: 1rem;
          background: rgba(255, 255, 255, 0.98);
          border: 1px solid rgba(22, 101, 52, 0.12);
          border-radius: 22px;
          box-shadow: 0 24px 55px rgba(15, 23, 42, 0.15);
        }

        .public-navbar__link {
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          color: #334155;
          font-weight: 800;
          border-radius: 999px;
          padding: 0.72rem 1rem !important;
        }

        .public-navbar__link:hover,
        .public-navbar__link.active {
          color: #166534;
          background: #f0fdf4;
        }

        .public-navbar__link-icon {
          color: #ea580c;
          font-size: 0.86rem;
        }

        .public-navbar__actions {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.65rem;
          margin-top: 1rem;
        }

        .public-navbar__login,
        .public-navbar__register {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          border-radius: 999px;
          font-weight: 900;
          padding: 0.68rem 1.15rem;
        }

        .public-navbar__login {
          color: #14532d;
          background: #ffffff;
          border: 1px solid rgba(22, 101, 52, 0.22);
        }

        .public-navbar__login:hover {
          color: #ffffff;
          background: #14532d;
        }

        .public-navbar__register {
          color: #ffffff;
          background: linear-gradient(135deg, #f97316, #ea580c);
          box-shadow: 0 14px 26px rgba(234, 88, 12, 0.25);
        }

        .public-navbar__register:hover {
          color: #ffffff;
          transform: translateY(-2px);
          box-shadow: 0 18px 34px rgba(234, 88, 12, 0.34);
        }

        .public-navbar__register:disabled,
        .public-navbar__register.disabled {
          color: #ffffff;
          background: #94a3b8;
          box-shadow: none;
          opacity: 1;
          cursor: not-allowed;
        }

        .public-navbar__user {
          display: inline-flex;
          align-items: center;
          gap: 0.65rem;
          border: 1px solid rgba(22, 101, 52, 0.15);
          border-radius: 999px;
          background: #ffffff;
          padding: 0.48rem 0.75rem;
          box-shadow: 0 12px 25px rgba(15, 23, 42, 0.08);
        }

        .public-navbar__user span {
          display: flex;
          flex-direction: column;
          text-align: left;
          line-height: 1.05;
        }

        .public-navbar__user small {
          color: #64748b;
          font-size: 0.68rem;
          font-weight: 700;
        }

        .public-navbar__user strong {
          color: #14532d;
          font-size: 0.84rem;
          font-weight: 900;
        }

        .public-navbar__dropdown {
          position: absolute;
          top: calc(100% + 0.75rem);
          right: 0;
          z-index: 1200;
          width: 220px;
          overflow: hidden;
          border-radius: 18px;
          background: #ffffff;
          border: 1px solid rgba(22, 101, 52, 0.14);
          box-shadow: 0 24px 50px rgba(15, 23, 42, 0.18);
          animation: navbarDrop 0.18s ease-out;
        }

        .public-navbar__dropdown-head {
          display: flex;
          flex-direction: column;
          padding: 1rem;
          background: #f0fdf4;
        }

        .public-navbar__dropdown-head strong {
          color: #14532d;
          font-size: 0.92rem;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .public-navbar__dropdown-head small {
          color: #64748b;
          font-size: 0.75rem;
          text-transform: capitalize;
        }

        .public-navbar__dropdown-item {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 0.65rem;
          border: 0;
          background: transparent;
          color: #334155;
          font-weight: 800;
          padding: 0.8rem 1rem;
          text-align: left;
          text-decoration: none;
        }

        .public-navbar__dropdown-item:hover {
          background: #f8fafc;
          color: #166534;
        }

        .public-navbar__dropdown-item.danger {
          color: #dc2626;
        }

        .public-navbar__dropdown-item.danger:hover {
          color: #b91c1c;
          background: #fef2f2;
        }

        @keyframes navbarDrop {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @media (min-width: 992px) {
          .public-navbar__actions {
            margin-top: 0;
          }

          .public-navbar__menu.show {
            margin-top: 0;
            padding: 0;
            border: 0;
            box-shadow: none;
            background: transparent;
          }
        }

        @media (max-width: 420px) {
          .public-navbar__brand-text small {
            max-width: 180px;
            white-space: normal;
          }

          .public-navbar__logo {
            width: 46px;
            height: 46px;
            border-radius: 15px;
          }

          .public-navbar__logo img {
            width: 42px;
            height: 42px;
          }
        }
      `}</style>
    </nav>
  );
};

export default Navbar;
